import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from '@/components/auth/AuthContext';
import { SettingsProvider } from '@/context/SettingsContext'; // Import SettingsProvider
import ProtectedRoute from '@/components/auth/ProtectedRoute';
import Login from '@/pages/Login';
import Dashboard from '@/pages/Dashboard';
import Documents from '@/pages/Documents'; // Import the new Documents page
import AnalyticsPage from '@/pages/AnalyticsPage'; // Import the new AnalyticsPage
import UsersPage from '@/pages/users/UsersPage'; // Import the UsersPage component
import UserDetailsPage from '@/pages/users/UserDetailsPage'; // Import the UserDetailsPage component
import ResetPassword from '@/pages/ResetPassword'; // Import the new ResetPassword page
import Home from '@/pages/Home';
import MainLayout from '@/components/layout/MainLayout';
import { Toaster } from 'react-hot-toast';
import GeneralSettings from "@/components/settings/GeneralSettings";
import QuerySettings from "@/components/settings/QuerySettings";
import DocumentProcessingSettings from "@/components/settings/DocumentProcessingSettings";
import NotificationSettings from "@/components/settings/NotificationSettings";
import SecuritySettings from "@/components/settings/SecuritySettings";
import IntegrationsSettings from "@/components/settings/IntegrationsSettings";
import AdvancedSettings from "@/components/settings/AdvancedSettings";
import SystemHealthModal from "@/components/settings/SystemHealthModal"; // Import Toaster for notifications
import { GlobalChatPopup } from '@/components/chat/GlobalChatPopup'; // Import GlobalChatPopup
import LicenseActivationPage from '@/pages/license/LicenseActivationPage';
import LicenseStatusPage from '@/pages/license/LicenseStatusPage';
import SubscriptionStatusPage from '@/pages/subscription/SubscriptionStatusPage';
import { env } from '@/config/env.ts';
import { useEffect, useState } from 'react';
import { initializeSystemConfig } from '@/services/systemConfigService';
import Chat from '@/pages/Chat.tsx';

function App() {
  const [configLoaded, setConfigLoaded] = useState(false);

  // Fetch system configuration on app initialization
  useEffect(() => {
    initializeSystemConfig().finally(() => {
      setConfigLoaded(true);
    });
  }, []);

  // Show loading state while fetching config
  if (!configLoaded) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading configuration...</p>
        </div>
      </div>
    );
  }
  return (
    <Router>
      <AuthProvider>
        <SettingsProvider> {/* Wrap the application with SettingsProvider */}
          <Toaster position="top-right" reverseOrder={false}
             toastOptions={{
            success: {
              duration: 6000, // Success toasts visible for 6 seconds
            },
            error: {
              duration: 8000, // Error toasts visible for 8 seconds
            },
            // Other toast options
          }}/> {/* Add Toaster component */}
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} /> {/* New route for password reset */}

            {env.isStandalone && (<Route path="/activate-license" element={<LicenseActivationPage />} />)} {/* License activation for standalone mode */}
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <Routes>
                      <Route path="/dashboard" element={<Dashboard />} />
                      <Route path="/chat" element={<Chat />} /> {/* New route for Chat Page */}
                      <Route path="/documents" element={<Documents />} /> {/* Use the Documents component */}
                      <Route path="/analytics" element={<AnalyticsPage />} /> {/* Use the AnalyticsPage component */}
                      <Route path="/users" element={<UsersPage />} /> {/* Use the UsersPage component */}
                      <Route path="/users/:id" element={<UserDetailsPage />} /> {/* Route for User Details Page */}
                      <Route path="/settings" element={<GeneralSettings />} /> {/* New route for Settings Page */}
                      <Route path="/settings/general" element={<GeneralSettings />} />{/* New route for Settings Page */}
                      <Route path="/settings/query" element={<QuerySettings />} />{/* New route for Settings Page */}
                      <Route path="/settings/doc-processing" element={<DocumentProcessingSettings />} />{/* New route for Settings Page */}
                      <Route path="/settings/notifications" element={<NotificationSettings />} />{/* New route for Settings Page */}
                      <Route path="/settings/security" element={<SecuritySettings />} />{/* New route for Settings Page */}
                      <Route path="/settings/integrations" element={<IntegrationsSettings />} />{/* New route for Settings Page */}
                      <Route path="/settings/advanced" element={<AdvancedSettings />} />{/* New route for Settings Page */}
                      <Route path="/settings/system-health" element={<SystemHealthModal />} />{/* New route for Settings Page */}
                      <Route path="/license" element={<LicenseStatusPage />} /> {/* License status for standalone mode */}
                      <Route path="/license/activate" element={<LicenseActivationPage />} /> {/* Activate license */}
                      <Route path="/subscription/status" element={<SubscriptionStatusPage />} /> {/* Subscription status for managed mode */}
                      <Route path="*" element={<div className="p-6 space-y-6">404 Not Found</div>} />
                    </Routes>
                  </MainLayout>
                </ProtectedRoute>
              }
            />
          </Routes>
          <GlobalChatPopup /> {/* Add the global chat popup component here */}
        </SettingsProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
